import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

let guaranteeBusy = false;
let pendingThemeUpdates = 0;
const cartRoot = window.Shopify?.routes?.root || '/';

// Wait for existing quantity changes before editing the guarantee.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (!event.promise) return;
  pendingThemeUpdates++;
  Promise.resolve(event.promise).catch(() => {}).finally(() => pendingThemeUpdates--);
});

async function cartRequest(path, body) {
  const response = await fetch(`${cartRoot}${path}`, {
    method: body ? 'POST' : 'GET',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json();
  if (!response.ok || data.errors) {
    throw new Error(typeof data.description === 'string' ? data.description : 'Could not update delivery guarantee. Please try again.');
  }
  return data;
}

class DeliveryGuarantee extends HTMLElement {
  connectedCallback() { this.addEventListener('change', this.handleChange); }
  disconnectedCallback() { this.removeEventListener('change', this.handleChange); }

  handleChange = async (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('.delivery-guarantee__checkbox')) return;
    const wanted = input.checked;
    const status = this.querySelector('.delivery-guarantee__status');
    if (guaranteeBusy || pendingThemeUpdates > 0) {
      input.checked = !wanted;
      status.textContent = 'Your cart is updating. Please try again in a moment.';
      return;
    }

    guaranteeBusy = true;
    input.disabled = true;
    status.textContent = 'Updating…';
    const components = [...document.querySelectorAll('cart-items-component')];
    const previousInert = components.map((element) => [element, element.inert]);
    previousInert.forEach(([element]) => { element.inert = true; });
    // Also guard a submit triggered from elsewhere during the request.
    const blockCheckout = (event) => {
      if (event.target instanceof HTMLFormElement && event.target.id === 'cart-form') event.preventDefault();
    };
    document.addEventListener('submit', blockCheckout, true);
    let previousSelected = !wanted;
    let mutationStarted = false;

    try {
      const currentCart = await cartRequest('cart.js');
      const productId = this.dataset.productId;
      const variantId = this.dataset.variantId;
      const guaranteeLines = currentCart.items.filter((item) => String(item.product_id) === productId);
      previousSelected = guaranteeLines.length > 0;
      if (wanted && !currentCart.items.some((item) => String(item.product_id) !== productId)) {
        throw new Error('Add a product to your cart before selecting delivery guarantee.');
      }
      const updates = {};
      // Preserve manually selected coverage quantities. OFF removes every guarantee line.
      guaranteeLines.forEach((item) => { updates[item.key] = wanted ? item.quantity : 0; });
      if (wanted && guaranteeLines.length === 0) updates[variantId] = 1;
      const sectionIds = [...new Set(components.map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
      mutationStarted = true;
      const updatedCart = await cartRequest('cart/update.js', {
        updates, sections: sectionIds, sections_url: window.location.pathname,
      });
      input.checked = updatedCart.items.some((item) => String(item.product_id) === productId);
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: wanted ? 'add' : 'remove',
        context: 'cart',
        lines: [],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
          detail: {
            sections: updatedCart.sections, items: updatedCart.items,
            itemCount: updatedCart.item_count, source: 'delivery-guarantee', didError: false,
          },
        }),
      }));
      status.textContent = '';
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    } catch (error) {
      input.checked = previousSelected;
      status.textContent = mutationStarted
        ? 'Could not confirm the update. Refresh the page to check your cart before trying again.'
        : error.message || 'Could not update delivery guarantee.';
    } finally {
      previousInert.forEach(([element, wasInert]) => { element.inert = wasInert; });
      document.removeEventListener('submit', blockCheckout, true);
      input.disabled = false;
      guaranteeBusy = false;
    }
  };
}

if (!customElements.get('delivery-guarantee')) customElements.define('delivery-guarantee', DeliveryGuarantee);
